import type {DefaultOptionType} from 'antd/es/select';

/** Lower rank = better match. 99 = no match. */
export function artistNameMatchRank(name: string, query: string): number {
    const n = name.trim().toLowerCase();
    const q = query.trim().toLowerCase();
    if (!q) {
        return 0;
    }
    if (n === q) {
        return 0;
    }
    if (n.startsWith(q)) {
        return 1;
    }
    if (n.split(/\s+/).some((word) => word.startsWith(q))) {
        return 2;
    }
    if (n.includes(q)) {
        return 3;
    }
    return 99;
}

export function artistSelectFilterOption(input: string, option?: DefaultOptionType): boolean {
    const label = String(option?.label ?? option?.value ?? '');
    return artistNameMatchRank(label, input) < 99;
}

/** rc-select/antd Select only pass (optionA, optionB) — no search context. */
export function artistSelectFilterSort(
    optionA: DefaultOptionType,
    optionB: DefaultOptionType,
    info?: {searchValue?: string},
): number {
    const labelA = String(optionA?.label ?? optionA?.value ?? '');
    const labelB = String(optionB?.label ?? optionB?.value ?? '');
    const query = info?.searchValue ?? '';
    const rankA = artistNameMatchRank(labelA, query);
    const rankB = artistNameMatchRank(labelB, query);
    if (rankA !== rankB) {
        return rankA - rankB;
    }
    return labelA.localeCompare(labelB, undefined, {sensitivity: 'base'});
}

/** Ant Design Select / ProFormSelect fieldProps for artist key_name search. */
export const artistSelectSearchFieldProps = {
    showSearch: true,
    optionFilterProp: 'label' as const,
    filterOption: artistSelectFilterOption,
    filterSort: artistSelectFilterSort,
};

export function compareArtistsBySearch(
    a: API.MusicDistribution.Artist,
    b: API.MusicDistribution.Artist,
    query: string,
): number {
    const rankA = Math.min(
        artistNameMatchRank(a.key_name, query),
        a.spotify_id ? artistNameMatchRank(a.spotify_id, query) : 99,
        a.apple_music_id ? artistNameMatchRank(a.apple_music_id, query) : 99,
    );
    const rankB = Math.min(
        artistNameMatchRank(b.key_name, query),
        b.spotify_id ? artistNameMatchRank(b.spotify_id, query) : 99,
        b.apple_music_id ? artistNameMatchRank(b.apple_music_id, query) : 99,
    );
    if (rankA !== rankB) {
        return rankA - rankB;
    }
    return a.key_name.localeCompare(b.key_name, undefined, {sensitivity: 'base'});
}

export function artistMatchesSearch(artist: API.MusicDistribution.Artist, query: string): boolean {
    const q = query.trim();
    if (!q) {
        return true;
    }
    return (
        artistNameMatchRank(artist.key_name, q) < 99 ||
        (artist.spotify_id != null && artistNameMatchRank(artist.spotify_id, q) < 99) ||
        (artist.apple_music_id != null && artistNameMatchRank(artist.apple_music_id, q) < 99)
    );
}
